import React from 'react';
import PropTypes from 'prop-types';
import hashHistory from '@src/History';

import RelementsIcon from 'relements/build/esm/ui/icon';
import Button from 'relements/build/esm/ui/button';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import WithTooltip from 'relements/build/esm/overlays/withTooltip';

// Decorators
import connection from '@src/connection';

import BotStore from '@src/store';

// Constants
import { ASK_HAPTIK_LINKS } from '@src/constants/HelpSection';
import { SCREEN_TYPES } from 'constants/MogamboConstants';
import * as ROUTES from 'constants/Routes';
import { MIX_PANEL } from '@src/const';

// Utils
import { isNewPipeline } from '@src/actions/_utils';
import { createAlert } from '@src/actions/BotActions';

// Components
import { getDomainStatus } from '@src/api/DomainAPI';
import EntitySearch from '@src/containers/Entities/EntitySearch';
import ShareBot from '@src/components/BotModals/ShareBot';
import UploadNodes from '@src/components/BotModals/UploadNodes';
import AddLanguage from '@src/components/BotModals/AddLanguage';
import EditBot from '@src/components/BotModals/EditBot';
import Icon from '@src/components/Icon';
import ShareIcon from '@src/components/Icon/share.svg';

// Actions
import { fetchTestConfiguration } from 'actions/DomainActions';

// Local
import LanguageButton from './LanguageButton';
import TransferModal from './components/TransferModal';
import * as maps from './maps';
import styles from './Header.scss';
import Breadcrumbs from './Breadcrumbs';
import SearchBar from './SearchBar';
// import LanguageButton from './LanguageButton/LanguageButton';
import TrainButton from './TrainButton';
import TransferButton from './components/TransferButton';
import PreviewTransferButton from './components/PreviewTransferButton';
import BotGlossaryModal from './BotGlossaryModal';
import OpenBusinessIcon from './icons/OpenBusiness.svg';

const PING_INTERVAL = 5000;
const NO_HEADER_PAGES = [
  'general-nodes',
  'general-responses',
  'step-priorities',
  'general-responses',
  'bots',
  'smart-skills',
  'businesses',
  'business-manager/*',
  'search',
];

/**
 * The Header component
 * It contains top level navigation items such the Breadcrumbs and the
 * buttons visible on the right side of the header menu
 *
 * Can possibly be converted to a functional component since it doesn't have
 * local function or state and doesn't use any lifecycle methods
 * @type {Object}
 */
@connection(maps.listenToStores, maps.getStateFromStores)
export default class Header extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      translationStatus: {},
      trainStatus: {},
      botGlossaryModalActive: false,
      transferModalActive: false,
      previewModalActive: false,
      isAugmentationInProgress: false,
      domainId: '',
      fetchTestConfigCalled: false,
      businessId: '',
    };
  }

  componentDidMount() {
    // make the api call the first time manually
    // then the interval takes over
    this._fetchStatus();
  }

  componentDidUpdate(prevProps, prevState) {
    // TODO: To optimise with a better approach to avoid multiple api calls
    if (
      this.props.currentScreen &&
      [
        SCREEN_TYPES.GRAPH,
        SCREEN_TYPES.STORIES,
        SCREEN_TYPES.SMALLTALK,
      ].includes(this.props.currentScreen)
    ) {
      if (typeof this._interval === 'undefined') {
        this._startListening();
      }
    } else {
      this._stopListening();
    }

    if (
      prevState.domainId &&
      !this.state.businessId &&
      !prevState.fetchTestConfigCalled
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        fetchTestConfigCalled: true,
      });
      fetchTestConfiguration(this.props.domain.id, (response) => {
        if (response.body) {
          const businessId = response.body.business_id;
          this.setState({
            businessId,
            fetchTestConfigCalled: true,
          });
        }
      });
    }
  }

  componentWillUnmount() {
    this._stopListening();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Update state if the bot is changed
    if (nextProps.domain.id !== prevState.domainId) {
      return {
        domainId: nextProps.domain.id,
        transferModalActive: false,
        previewModalActive: false,
        isAugmentationInProgress: false,
        businessId: '',
        fetchTestConfigCalled: false,
      };
    }
    // Return null if the state hasn't changed
    return null;
  }

  render() {
    const path = window.location.hash;

    const noHeaders = NO_HEADER_PAGES.map((disabledPage) => {
      if (disabledPage.includes('/*'))
        return path.includes(disabledPage.split('/*')[0]);
      return path.split('/').pop() === disabledPage;
    });

    if (noHeaders.includes(true)) return null;

    return (
      <div className={styles.header}>
        <div className={styles.headerColumn}>{this._renderLeft()}</div>
        <div className={styles.headerColumn}>{this._renderCTAs()}</div>
        <BotGlossaryModal
          active={this.state.botGlossaryModalActive}
          domain={this.props.domain}
          toggleGlossaryModal={this._setBotGlossaryModalInactive}
        />
        <TransferModal
          isAugmentationInProgress={this.state.isAugmentationInProgress}
          active={this.state.transferModalActive}
          onClose={this._closeTransferModal}
          onAugmentationStart={this._fetchBotStatus}
          onTransferStart={this._fetchBotStatus}
          domain={this.props.domain}
          showPreview={this._showPreviewModal}
        />
      </div>
    );
  }

  _showTransferModal = () => {
    this.setState({
      transferModalActive: true,
    });
  };

  _closeTransferModal = () => {
    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.PREVIEW_TRANSFER.TRANSFER_JUMP_OUT,
    });
    this.setState({
      transferModalActive: false,
      previewModalActive: false,
    });
  };

  _showPreviewModal = () => {
    this.setState({
      transferModalActive: false,
      previewModalActive: true,
    });
  };

  _onPreviewModalClose = () => {
    this.setState({
      previewModalActive: false,
    });
  };

  /**
   * This method is called after augmentation process is started from TransferModal.
   */
  _fetchBotStatus = () => {
    this._fetchStatus();
    this._closeTransferModal();
  };

  _renderHelpMenu = () => {
    return (
      <div className={styles.helpMenuContainer}>
        <div className={styles.helpMenuTitle}>Help</div>
        <div className={styles.helpMenuBody}>
          {ASK_HAPTIK_LINKS.map((row, index) => {
            return (
              <div className={styles.helpMenuLink} key={index}>
                <Button
                  onClick={() => window.open(row.url, '_blank')}
                  className={styles.fullWidthButton}
                >
                  <Icon iconType="question" />
                  <span className={styles.helpMenuLinkTitle}>{row.title}</span>
                </Button>
              </div>
            );
          })}
        </div>
        <div className={styles.helpMenuFooter}>
          <a href="http://ask.hellohaptik.com" target="_blank" rel="noreferrer">
            View All Questions
          </a>
        </div>
      </div>
    );
  };

  _renderLeft = () => {
    let crumbs = [
      { ...this.props.meta.domain, key: 'botId', route: ROUTES.STORIES },
      { ...this.props.story, key: 'storyId', route: ROUTES.SUBSTORIES },
    ];

    switch (this.props.currentScreen) {
      case SCREEN_TYPES.HOME:
        return null;
      case SCREEN_TYPES.STORIES:
      case SCREEN_TYPES.GRAPH:
        return <Breadcrumbs crumbs={crumbs} meta={this.props.meta} />;
      case SCREEN_TYPES.SMALLTALK:
        crumbs = [
          ...crumbs,
          { title: 'Small Talk', key: 'smallTalk', route: ROUTES.SMALLTALK },
        ];
        return <Breadcrumbs crumbs={crumbs} meta={this.props.meta} />;
      case SCREEN_TYPES.BUSINESS_MANAGER:
        crumbs = [
          { title: 'Business Manager', route: '/businesses' },
          { title: this.props.businessName },
        ];
        return <Breadcrumbs crumbs={crumbs} meta={this.props.meta} />;
      default:
        return null;
    }
  };

  _renderCenter = () => {
    switch (this.props.currentScreen) {
      case SCREEN_TYPES.SMALLTALK:
      case SCREEN_TYPES.STORIES:
      case SCREEN_TYPES.GRAPH:
        return <SearchBar />;
      default:
        return null;
    }
  };

  _renderCTAs = () => {
    const { translationStatus, trainStatus } = this.state;
    const isSuperman = isNewPipeline(this.props.domain);

    switch (this.props.currentScreen) {
      case SCREEN_TYPES.HOME:
        return null;
      case SCREEN_TYPES.STORIES:
      case SCREEN_TYPES.GRAPH:
      case SCREEN_TYPES.SMALLTALK:
        return (
          <div className={styles.headerCTAs}>
            <SearchBar domain={this.props.domain} />

            {isSuperman &&
              (!this.state.businessId ? (
                <WithTooltip
                  tooltip="Business link unavailable."
                  position="BOTTOM"
                >
                  {this._renderOpenBusinessButton()}
                </WithTooltip>
              ) : (
                this._renderOpenBusinessButton()
              ))}
            <TrainButton domain={this.props.domain} status={trainStatus} />

            <PreviewTransferButton
              domainTitle={this.props.meta.domain.title}
              trainStatus={trainStatus}
              onTransferClick={this._showTransferModal}
              previewModalActive={this.state.previewModalActive}
              onPreviewModalClose={this._onPreviewModalClose}
            />

            {!isSuperman && (
              <LanguageButton
                domain={this.props.domain}
                currentLanguage={this.props.currentLanguage}
                supportedLanguages={this.props.supportedLanguages}
                allLanguages={this.props.allLanguages}
                onLanguageSelect={this._updateCurrentLanguage}
                status={translationStatus}
                onChange={(translationStatus) =>
                  this.setState({ translationStatus })
                }
              />
            )}
            <Button
              type={Button.TYPES.OUTLINE}
              onClick={this._viewCredentials}
              size="big"
            >
              <RelementsIcon
                className={styles.shareIcon}
                size={17}
                src={ShareIcon}
              />
            </Button>
            <ContextMenu.Button
              type={Button.TYPES.OUTLINE}
              ref={(ref) => {
                if (ref) this._moreMenuRef = ref;
              }}
              size="big"
              title="More"
              offset={{ top: 40, left: -12 }}
            >
              <ContextMenu.Item onClick={this._editBot} size="big">
                Edit Bot
              </ContextMenu.Item>
              <ContextMenu.Item onClick={this._extractBot} size="big">
                Extract Bot
              </ContextMenu.Item>
              <ContextMenu.Item onClick={this.showEntityList} size="big">
                List of Entities
              </ContextMenu.Item>
              <ContextMenu.Item onClick={this._navToNodePriority} size="big">
                Step Priority
              </ContextMenu.Item>
              <ContextMenu.Item onClick={this._uploadNodes} size="big">
                Upload Nodes from File
              </ContextMenu.Item>
              {isSuperman && (
                <ContextMenu.Item onClick={this._botGlossary} size="big">
                  Edit Language Glossary
                </ContextMenu.Item>
              )}
            </ContextMenu.Button>
          </div>
        );
      default:
        return this.props.headerData.actions || null;
    }
  };

  _renderOpenBusinessButton = () => {
    return (
      <Button
        size="big"
        type={Button.TYPES.OUTLINE}
        onClick={this._openBusiness}
        disabled={!this.state.businessId}
      >
        <RelementsIcon
          src={OpenBusinessIcon}
          className={`${styles.headerCTAsOpenBusinessIcon} ${
            !this.state.businessId && styles.headerCTAsOpenBusinessIconDisabled
          }`}
        />
        Open Business
      </Button>
    );
  };

  _openBusiness = async () => {
    const { businessId } = this.state;
    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.BOT.OPEN_BUSINESS,
    });

    if (businessId) {
      hashHistory.push(`/business-manager/${businessId}`);
    } else {
      createAlert('Error opening business, try again later.', null, 'NEGATIVE');
    }
  };

  showEntityList = (e) => {
    this._moreMenuRef.handleClose(e);
    maps.openComponentModal(
      <EntitySearch
        hideCreateNewEntity
        searchFilters={{
          search_type: 'bot',
          bot_name: this.props.domain.title,
        }}
        initSearch
      />,
      'Entities used in this Bot',
      null,
      'CROSS'
    );
  };

  _fetchStatus = async (domain = this.props.domain) => {
    if (!domain.title) return;

    try {
      const {
        translation_status: translationStatus = {},
        train_status: trainStatus = {},
        augmentation_status: augmentationStatus = {},
      } = (await getDomainStatus(domain.title)).body;
      // augmentation_in_progress will be true only when the augment process is in progress.
      const isAugmentationInProgress =
        augmentationStatus.augmentation_in_progress;
      if (this.state.isAugmentationInProgress && !isAugmentationInProgress) {
        this._closeTransferModal();
        // If isAugmentationInProgress was true and now it is false, it means the augmentation is now completed.
        createAlert(
          "Smart variants added. We've sent you an email with the list of updated nodes.",
          null,
          'POSITIVE'
        );
      }
      this.setState({
        translationStatus,
        trainStatus,
        isAugmentationInProgress,
      });
    } catch (e) {
      // silently ignore
    }
  };

  _startListening = () => {
    clearInterval(this._interval);
    this._interval = setInterval(this._fetchStatus, PING_INTERVAL);
  };

  _stopListening = () => {
    clearInterval(this._interval);
    this._interval = undefined;
  };

  /**
   * onClick handler for the logout Button
   * Opens a confirmation dialog
   * @return {[type]} [description]
   */
  _logout = () => {
    maps.logout();
  };

  /**
   * onClick handler for the Edit Button
   * Opens a confirmation dialog
   * @return {[type]} [description]
   */
  _editBot = (e) => {
    this._moreMenuRef.handleClose(e);
    maps.openComponentModal(
      <EditBot
        botData={this.props.domain}
        allLanguages={this.props.allLanguages}
      />,
      'Edit Bot',
      null,
      'close'
    );
  };

  /**
   * onClick handler for the Extract Button
   * Opens a confirmation dialog
   * @return {[type]} [description]
   */
  _extractBot = (e) => {
    this._moreMenuRef.handleClose(e);
    maps.confirmExtractBot(this.props.domain.title.toLowerCase());
  };

  /**
   * onClick handler for the Node Priority Button
   * Navigates to the story general-nodes overlay page
   * @return {[type]} [description]
   */
  _navToNodePriority = () => {
    const url = `/bot/${this.props.domain.id}/step-priorities`;
    hashHistory.push(url);
  };

  _viewCredentials = (e) => {
    this._moreMenuRef.handleClose(e);
    maps.openComponentModal(
      <ShareBot botId={this.props.domain.id} />,
      'Test Credentials',
      null,
      'close'
    );
  };

  _uploadNodes = (e) => {
    this._moreMenuRef.handleClose(e);
    maps.openComponentModal(<UploadNodes />, 'Upload Step', null, 'CROSS');
  };

  _botGlossary = (e) => {
    this._moreMenuRef.handleClose(e);
    this.setState({
      botGlossaryModalActive: !this.state.botGlossaryModalActive,
    });
  };

  _setBotGlossaryModalInactive = () => {
    this.setState({
      botGlossaryModalActive: false,
    });
  };

  _updateCurrentLanguage = (selectedLanguage) => {
    // Language Source is our primary identifier for all language changes
    // Hence we use the same as our current source and script when editing on Mogambo
    maps.updateCurrentLanguage(selectedLanguage, selectedLanguage);
    maps.closeComponentModal();
  };

  _showAddLanguageModal = () => {
    maps.openComponentModal(<AddLanguage />, 'Add Language', null, 'CROSS');
  };
}

Header.propTypes = {
  meta: PropTypes.object,
  domain: PropTypes.object,
  story: PropTypes.object,
  businessName: PropTypes.string,
  currentLanguage: PropTypes.shape({
    languageSource: PropTypes.string,
    languageScript: PropTypes.string,
  }),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  allLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      native_name: PropTypes.string,
    })
  ),
  langaugeSupportedDomains: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      domain_name: PropTypes.string,
    })
  ),
};
